/*
  Сведения организации, найденные в присланных файлах: загрузка и запись.

  Состояние по каждому файлу своё - что нашлось, что записано последним
  нажатием, какая ошибка. Файлов с настройками обычно один-два (карточка
  предприятия, договор, перечень домов), поэтому каждый читается отдельным
  запросом при появлении в списке.

  С 21.10.2026 список таких файлов даёт СЕРВЕР отбором `has_settings=true`: таблица
  файлов показывает одну страницу, и отбирать «файлы со сведениями организации» по ней
  в браузере значило бы потерять карточку, как только файл уедет на вторую страницу.
  Список перечитывается, когда меняется отпечаток файлов (`revision`): файл прочитан,
  удалён или добавлен. `null` - вкладка ещё не знает, что с файлами, ничего не спрашиваем.
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../../api/client.js'

// Карточек больше страницы не бывает: предел страницы сервера - 500.
const ALL_WITH_SETTINGS = { has_settings: true, limit: 500 }

export default function useFoundSettings(revision) {
  const [files, setFiles] = useState([])
  const [found, setFound] = useState({})
  const [error, setError] = useState(null)
  const ids = files.map((file) => file.id)
  const key = ids.join(',')

  useEffect(() => {
    if (revision === null) return undefined
    /* Ответ запоздавшего запроса не затирает свежий (23.10.2026, находка проверки; так же, как
       в `useOwnScans.js`): отпечаток файлов меняется часто - файл прочитан, удалён, добавлен, -
       и прежний ответ, пришедший позже, вернул бы карточку удалённого файла. */
    let alive = true
    // Сбой чтения списка карточек виден (`error`); прежние карточки остаются на экране.
    api
      .uploads(ALL_WITH_SETTINGS)
      .then((answer) => {
        if (!alive) return
        setFiles(answer.items)
        setError(null)
      })
      .catch((failure) => {
        if (alive) setError(failure)
      })
    return () => {
      alive = false
    }
  }, [revision])

  const load = useCallback((id) => {
    api
      .uploadSettings(id)
      .then((shown) => setFound((all) => ({ ...all, [id]: { ...all[id], shown, error: null } })))
      .catch((error) => setFound((all) => ({ ...all, [id]: { ...all[id], error } })))
  }, [])

  useEffect(() => {
    key.split(',').filter(Boolean).forEach(load)
  }, [key, load])

  const apply = async (id, choice) => {
    setFound((all) => ({ ...all, [id]: { ...all[id], busy: true, error: null } }))
    try {
      const applied = await api.applyUploadSettings(id, choice)
      setFound((all) => ({ ...all, [id]: { ...all[id], busy: false, applied } }))
      load(id)
    } catch (error) {
      setFound((all) => ({ ...all, [id]: { ...all[id], busy: false, error } }))
    }
  }

  return { files, found, error, apply }
}
